<template>
  <div class="modal fade" id="vpnModal" tabindex="-1" aria-hidden="true" ref="modalEl">
    <div class="modal-dialog modal-dialog-centered w-[460px]">
      <div class="modal-content" v-if="props.license">
        <div class="modal-header">
          <h1 class="modal-title fs-5">VPN for {{ props.license.host_domain }}:{{ props.license.http_port }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body px-4">
          <div v-if="loading" class="text-gray-500 py-3">Loading…</div>
          <template v-else-if="vpn && vpn.supported">
            <!-- ProProxyUI: WireGuard and OpenVPN are always ready, one file each -->
            <div v-if="vpn.ppui" class="space-y-3">
              <div v-if="vpn.wg" class="space-y-1" style="border: 1px solid var(--ui-line); border-radius: 8px; padding: 8px 10px;">
                <div class="flex items-center justify-between"><b>WireGuard</b><span class="font-mono text-sm">{{ vpn.host }}:{{ vpn.wg.port }}</span></div>
                <div class="text-sm text-gray-500">No login needed: the keys are inside the file.</div>
                <div class="flex gap-2 pt-1">
                  <button class="btn btn-primary btn-sm" :disabled="busy" @click="download('wg')">Download .conf file</button>
                  <button class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="newKeys">New keys</button>
                </div>
              </div>
              <div v-if="vpn.ovpn" class="space-y-1" style="border: 1px solid var(--ui-line); border-radius: 8px; padding: 8px 10px;">
                <div class="flex items-center justify-between"><b>OpenVPN</b><span class="font-mono text-sm">{{ vpn.host }}:{{ vpn.ovpn.port }} {{ vpn.ovpn.proto }}</span></div>
                <div v-if="vpn.ovpn_login" class="text-sm text-gray-500">Login: your proxy username and password (<span class="font-mono">{{ vpn.user }}</span>)</div>
                <div v-else class="text-sm text-gray-500">OpenVPN logs in with a username and password. Set a username:password login under Authentication to use it.</div>
                <div class="pt-1"><button class="btn btn-primary btn-sm" :disabled="busy || !vpn.ovpn_login" @click="download('ovpn')">Download .ovpn file</button></div>
              </div>
              <div class="text-sm text-gray-500">Data today: {{ vpn.used_mb }} MB<span v-if="vpn.quota_gb"> of {{ vpn.quota_gb }} GB</span>, shared by the proxy and the VPN.</div>
              <div class="text-xs text-gray-500">New keys, or a new proxy password, make a new WireGuard file: download it again afterwards.</div>
            </div>
            <!-- enabled -->
            <div v-else-if="vpn.enabled" class="space-y-2">
              <div class="flex items-center justify-between">
                <span>{{ vpn.type_label }} is <b class="text-green-600">enabled</b></span>
                <span v-if="vpn.blocked" class="text-red-500 text-sm">paused: daily allowance used</span>
              </div>
              <div class="text-sm">Server <span class="font-mono">{{ vpn.host }}:{{ vpn.port }}</span></div>
              <div v-if="vpn.type === 'opvn'" class="text-sm">Login: your proxy username and password (<span class="font-mono">{{ vpn.user }}</span>)</div>
              <div v-else class="text-sm">No login needed: the keys are inside the config file.</div>
              <div class="text-sm text-gray-500">
                Data today: proxy {{ vpn.proxy_used_mb }} MB + VPN {{ vpn.vpn_used_mb }} MB
                <span v-if="vpn.quota_mb"> of {{ vpn.quota_mb }} MB shared allowance ({{ vpn.remaining_mb }} MB left)</span>
                <span v-else> (no daily limit)</span>
              </div>
              <div class="flex gap-2 pt-2">
                <button class="btn btn-primary btn-sm" :disabled="busy" @click="download">
                  Download {{ vpn.type === 'wg' ? '.conf' : '.ovpn' }} file
                </button>
                <button class="btn btn-outline-danger btn-sm" :disabled="busy" @click="disable">Disable VPN</button>
              </div>
              <div class="text-xs text-gray-500 pt-1">Disabling and re-enabling issues new keys, so previously downloaded files stop working. Data used through the VPN counts against the same daily allowance as the proxy.</div>
            </div>
            <!-- not enabled -->
            <div v-else class="space-y-3">
              <div v-if="vpn.server_off" class="text-sm" style="color: var(--ui-warn-ink); background: var(--ui-warn-soft); border: 1px solid var(--ui-warn); border-radius: 8px; padding: 8px 10px;">
                The VPN is switched off on the server at the moment. Turn it on again below, then download a fresh file.
              </div>
              <div class="text-sm">Turn on a VPN that uses this proxy's mobile connection. Pick one type; it can be changed later.</div>
              <v-radio-group v-model="pickedType" inline hide-details density="compact">
                <v-radio v-for="t in vpn.types" :key="t.value" :label="t.name" :value="t.value"></v-radio>
              </v-radio-group>
              <div class="text-xs text-gray-500">
                WireGuard: fastest, keys in the file, no login. OpenVPN: logs in with your proxy username and password.
                VPN data counts against the same daily allowance as the proxy<span v-if="vpn.quota_mb"> ({{ vpn.quota_mb }} MB)</span>.
              </div>
              <button class="btn btn-primary btn-sm" :disabled="busy || !pickedType" @click="enable">Enable {{ pickedType === 'wg' ? 'WireGuard' : pickedType === 'opvn' ? 'OpenVPN' : '' }}</button>
            </div>
          </template>
          <div v-else class="text-gray-500 py-3">VPN is not available for this proxy.</div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import { useProxyStore, useAlertStore } from "@/stores"

const props = defineProps({ license: { type: Object, required: true } });
const emit = defineEmits(['close']);
const proxyStore = useProxyStore();
const alertStore = useAlertStore();

const vpn = ref(null);
const loading = ref(false);
const busy = ref(false);
const pickedType = ref('wg');

async function load() {
  if (!props.license) { vpn.value = null; return; }
  loading.value = true;
  vpn.value = await proxyStore.getVpn(props.license.uuid);
  if (vpn.value?.server_off && vpn.value.type) pickedType.value = vpn.value.type;   // offer the same type again
  loading.value = false;
}
// Load only while this dialog is open (the licence prop is shared with every dialog on the page).
const modalEl = ref(null);
const isOpen = () => !!modalEl.value && modalEl.value.classList.contains('show');
watch(() => props.license, () => { if (isOpen()) load(); });
// while the window stays open the state is re-read every 10 minutes (quietly)
let refreshTimer = null;
const refresh = async () => { if (!isOpen() || busy.value || !props.license) return; const next = await proxyStore.getVpnQuiet(props.license.uuid); if (next) vpn.value = next; };
onMounted(() => { modalEl.value && modalEl.value.addEventListener('shown.bs.modal', load); refreshTimer = setInterval(refresh, 10 * 60 * 1000); });
onBeforeUnmount(() => { modalEl.value && modalEl.value.removeEventListener('shown.bs.modal', load); clearInterval(refreshTimer); });

async function enable() {
  busy.value = true;
  const res = await proxyStore.enableVpn(props.license.uuid, pickedType.value);
  if (res) { vpn.value = res; alertStore.success("VPN enabled. Download the file to connect."); emit('close'); }
  busy.value = false;
}
async function disable() {
  if (!window.confirm("Disable the VPN? Your downloaded file will stop working.")) return;
  busy.value = true;
  const res = await proxyStore.disableVpn(props.license.uuid);
  if (res) { vpn.value = res; alertStore.success("VPN disabled"); emit('close'); }
  busy.value = false;
}
async function newKeys() {
  if (!window.confirm("Make new WireGuard keys? Your downloaded .conf file will stop working.")) return;
  busy.value = true;
  const res = await proxyStore.enableVpn(props.license.uuid, 'wg-new-keys');
  if (res) { vpn.value = res; alertStore.success("New keys made. Download the new .conf file."); }
  busy.value = false;
}
async function download(type) {
  busy.value = true;
  const f = await proxyStore.vpnFile(props.license.uuid, typeof type === 'string' ? type : undefined);   // the old button passes the click event
  if (f && f.content) {
    const blob = new Blob([f.content], { type: 'application/octet-stream' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = f.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }
  busy.value = false;
}
</script>
